import { useMemo } from "react";
import { useDocuments } from "./use-documents";
import { useAudits } from "./use-audits";
import { AUDIT_STATUS_CONFIG } from "@/components/audits/constants";
import type { EntityContext } from "@/stores/chat-context-store";

const MAX_PER_SECTION = 4;

export interface MentionItem {
  entity: EntityContext;
  subtitle?: string;
}

export interface MentionSection {
  label: string;
  items: MentionItem[];
}

interface UseMentionSearchReturn {
  sections: MentionSection[];
  flatItems: MentionItem[];
  isLoading: boolean;
}

export function useMentionSearch(query: string): UseMentionSearchReturn {
  const { documents, isLoading: docsLoading } = useDocuments();
  const { audits, isLoading: auditsLoading } = useAudits();

  const sections = useMemo(() => {
    const q = query.toLowerCase();
    const result: MentionSection[] = [];

    // Documents section
    if (documents) {
      const matched = documents
        .filter((doc) => doc.title.toLowerCase().includes(q))
        .slice(0, MAX_PER_SECTION)
        .map(
          (doc): MentionItem => ({
            entity: { type: "document", id: doc.id, label: doc.title },
            subtitle: doc.type,
          }),
        );
      if (matched.length > 0) {
        result.push({ label: "Documents", items: matched });
      }
    }

    // Audits section
    if (audits) {
      const matched = audits
        .filter((audit) => audit.displayName.toLowerCase().includes(q))
        .slice(0, MAX_PER_SECTION)
        .map(
          (audit): MentionItem => ({
            entity: {
              type: "audit",
              id: audit.id,
              label: audit.displayName,
            },
            subtitle: AUDIT_STATUS_CONFIG[audit.status].label,
          }),
        );
      if (matched.length > 0) {
        result.push({ label: "Audits", items: matched });
      }
    }

    return result;
  }, [documents, audits, query]);

  const flatItems = useMemo(() => sections.flatMap((s) => s.items), [sections]);

  return {
    sections,
    flatItems,
    isLoading: docsLoading || auditsLoading,
  };
}
